import React, {useEffect, useMemo, useRef, useState} from "react";
import {type TemplateDefinition} from "data/template-catalog";
import {BannerRenderer} from "../../../grid/BannerRenderer";
import {PreviewBoundary} from "../../../grid/ui/PreviewBoundary";
import {previewBanner} from "./previewBanner";

/** The width of a preview on its card, in CSS px. */
export const PREVIEW_WIDTH = 280;

function readOnly(): never {
  throw new Error("Template previews are read-only");
}

/** Keeps a broken preview on its own card: the other cards keep drawing. */
class PreviewErrorBoundary extends React.Component<
  {templateKey: string; children: React.ReactNode},
  {error: Error | null}
> {
  state = {error: null as Error | null};

  static getDerivedStateFromError(error: Error) {
    return {error};
  }

  componentDidCatch(error: Error) {
    console.error(`[templates] Preview of "${this.props.templateKey}" failed`, error);
  }

  render() {
    if (this.state.error) {
      return (
        <p className="admin-template-preview-error" role="alert">
          The preview could not draw: {this.state.error.message}
        </p>
      );
    }
    return this.props.children;
  }
}

function LiveBanner({definition}: {definition: TemplateDefinition}) {
  // Unique per card: two channels can draw one id (Email01), and a key is
  // unique. No hyphen, so no template's `[id^="Id-"]` CSS matches it.
  const instanceId = `${definition.templateId}Fleet${definition.key.replace(/[^A-Za-z0-9]/g, "")}`;
  const banner = useMemo(() => previewBanner(definition, instanceId), [definition, instanceId]);
  return (
    <PreviewBoundary readOnly>
      <BannerRenderer banner={banner} onChange={readOnly} zoom={definition.width / PREVIEW_WIDTH} />
    </PreviewBoundary>
  );
}

/**
 * A live, read-only render of one template at card size: flat dark grey,
 * placeholder texts. It draws only near the viewport, because ninety live
 * banners at once make the tab slow.
 */
export function TemplatePreview({definition}: {definition: TemplateDefinition}) {
  const box = useRef<HTMLDivElement>(null);
  const [isNear, setIsNear] = useState(false);
  useEffect(() => {
    const element = box.current;
    if (!element) throw new Error("Template preview box is missing");
    const observer = new IntersectionObserver(
      (entries) => setIsNear(entries.some((entry) => entry.isIntersecting)),
      {rootMargin: "1200px 0px"}
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={box}
      className="admin-template-preview"
      style={{width: PREVIEW_WIDTH, height: Math.round(definition.height / (definition.width / PREVIEW_WIDTH))}}
      data-template-preview={definition.templateKey}
      data-preview-drawn={isNear ? "true" : "false"}
    >
      {isNear && (
        <PreviewErrorBoundary templateKey={definition.key}>
          <LiveBanner definition={definition} />
        </PreviewErrorBoundary>
      )}
    </div>
  );
}
